import React, { useRef, useContext } from 'react'
import styled from '@emotion/styled'
import { message } from 'antd'
import { usePathname } from 'next/navigation'
import { LinkContext } from 'app/components/providers/linkProvider'
import { ReactComponent as FileIcon } from '../../images/svg/file.svg'
import { ReactComponent as CheckCircleIcon } from '../../images/svg/check-circle.svg'
import { ReactComponent as PdfSvg } from '../../images/svg/pdfIcon.svg'

import { getUrlSearchString } from '../utils'
import { maxFileSize, useUploadResumeContext } from './context'
import UploadResumeWrap from './Wrap'
import { Gtag } from 'helpers/gtagEvent'
import { Button } from 'app/(new-pages)/components/Button/pc/index.pc'
import { RouterContext } from '../routerProvider'

const UploadSuccess = (props: { lang: any; userDetail: any; getUserInfo: () => Promise<any> }) => {
  const { getUserInfo, userDetail, lang = {} } = props
  const profile = lang.profile || {}
  const inputRef = useRef(null)
  const { fileData, uploadResumeFile } = useUploadResumeContext()
  const { push } = useContext(LinkContext)
  const pathname = usePathname()

  const onChange = (event) => {
    const file = event.target.files[0]
    if (!file) {
      return
    }
    if (file.size > maxFileSize) {
      message.error(profile?.uploadResumeModal?.errorTip)
      return false
    }
    const resumeId = userDetail?.resume?.id || fileData?.data?.id
    uploadResumeFile({ file, resumeId: resumeId })
  }

  const handleUpload = () => {
    inputRef.current.click()
  }

  const handSubmit = () => {
    Gtag('register_upload_resume')
    setTimeout(() => {
      const searchWords = getUrlSearchString(1)
      getUserInfo && getUserInfo()
      push(`${pathname}/thank-you/${searchWords}`)
    }, 200)
  }

  const handleBack = () => {
    if (typeof window !== 'undefined') {
      window.history.back()
    }
  }

  return (
    <UploadResumeWrap
      {...props}
      title='Upload your resume file'
      footer={
        <FooterWrap>
          <BackButton onClick={handleBack}>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M5.25 13.5L1.5 9L5.25 4.5'
                stroke='#121212'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>{' '}
            <p className='countdownText'>{profile.back}</p>
          </BackButton>
          <div className='footer-btns'>
            <input
              type='file'
              accept='.pdf, .doc, .docx'
              ref={inputRef}
              onChange={onChange}
              style={{ display: 'none' }}
              hidden
            />
            <Button
              variant='whiteBoxShadowBtn'
              btnSize='size44'
              className='re-upload'
              onClick={handleUpload}
            >
              {profile?.uploadResumeModal?.reUploadBtn}
            </Button>
            <Button variant='primaryBtn' btnSize='size44' className='confirm' onClick={handSubmit}>
              {profile?.confirm}
            </Button>
          </div>
        </FooterWrap>
      }
    >
      <ContentWrap>
        <div className='modal-content'>
          <div className='file-info'>
            {fileData?.data?.name?.indexOf('word') > -1 ? WordPdf : <PdfSvg />}
            <span className='file-name'>{fileData?.data?.name || 'xxxxx.pdf'}</span>
            <div className='upload-success'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='33'
                height='32'
                viewBox='0 0 33 32'
                fill='none'
              >
                <circle cx='17.0654' cy='18.1348' r='10.0068' fill='white' />
                <path
                  d='M16.5013 29.332C23.8651 29.332 29.8346 23.3624 29.8346 15.9986C29.8346 8.63482 23.8651 2.66528 16.5013 2.66528C9.13751 2.66528 3.16797 8.63482 3.16797 15.9986C3.16797 23.3624 9.13751 29.332 16.5013 29.332ZM16.2735 20.8862C16.025 21.1648 15.6713 21.3264 15.2981 21.3319C14.9249 21.3375 14.5665 21.1863 14.31 20.9152L9.70833 16.0525L11.6452 14.2196L15.2493 18.0282L21.7465 10.7434L23.7366 12.5184L16.2735 20.8862Z'
                  fill='#29CC6A'
                />
              </svg>
              {profile?.uploadResumeModal?.uploadSuccess}
            </div>
          </div>
        </div>
      </ContentWrap>
    </UploadResumeWrap>
  )
}

const media = {
  sm: `@media (max-width: 576px)`,
  md: `@media (min-width: 800px)`,
  msd: `@media (max-width: 799px)`
}

const FooterWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 32px;

  .footer-btns {
    display: flex;
    align-items: center;
    gap: 12px;
  }
`

const ContentWrap = styled.div`
  .modal-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 24px;
    flex: 1 0 0;
    align-self: stretch;
    border-radius: 16px;
    border: 3px dashed #ebeef0;
    background: #fdfdfd;
  }

  .file-info {
    display: flex;
    padding: 62px 0;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    align-self: stretch;
    ${media.msd} {
      padding: 48px 0 0 0;
    }
    .file-name {
      color: #000;
      text-align: center;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 24px;
    }
    .upload-success {
      font-size: 20px;
      font-style: normal;
      font-weight: 600;
      line-height: 28px;
      color: #111212;
      display: flex;
      align-items: center;
      gap: 10px;
      margin-top: 32px;
    }
  }

  .file-info img {
    width: 40px;
    height: 40px;
    margin-right: 10px;
  }
`

const BackButton = styled.div`
  display: inline-flex;
  height: 44px;
  padding: 10px 10px 10px 0;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  p.countdownText {
    color: #111212;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 22px;
  }
`

export default UploadSuccess

const WordPdf = (
  <svg xmlns='http://www.w3.org/2000/svg' width='48' height='58' viewBox='0 0 48 58' fill='none'>
    <path
      d='M0.890625 6.77788C0.890625 3.34144 3.67641 0.555664 7.11285 0.555664H24.0017H29.7795H30.168C31.8057 0.555664 33.3773 1.20131 34.5421 2.35258L45.2647 12.9511C46.4473 14.1201 47.1129 15.7137 47.1129 17.3765V29.0001V51.2223C47.1129 54.6588 44.3271 57.4446 40.8906 57.4446H7.11286C3.67642 57.4446 0.890625 54.6588 0.890625 51.2223V6.77788Z'
      fill='#0066FF'
    />
  </svg>
)
